import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import "./Signup.scss";
import { imageList } from "@/core/utils/imageList";
import { ROUTES } from "@/core/constants/app";
import { SEO } from "@/core/components/SEO/SEO";
import { GoogleOAuthProvider } from "@react-oauth/google";
import GoogleLoginButton from "../auth/components/GoogleLoginButton";
import FacebookLoginButton from "../auth/components/FacebookLoginButton";
import LinkedInLoginButton from "../auth/components/LinkedInLoginButton";
import { VITE_PUBLIC_GOOGLE_LOGIN_CLIENT_ID } from "@/core/constants/env";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { reCaptchaV3ValidationService } from "@/core/services/reCaptchaV3/reCaptchaV3Validation";

interface SignupFormData {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  password: string;
  confirmPassword: string;
  marketingUpdates: boolean;
  termsAccepted: boolean;
}

export const Signup = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(
    null,
  );
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm<SignupFormData>();

  const password = watch("password");

  const onSubmitInitialForm = async (data: SignupFormData) => {
    setIsRegistering(true);
    setRegistrationError(null);
    try {
      // Format payload for initial registration with only basic info
      let payload: Record<string, any> = {
        // langcode: [{value: 'en'}],
        name: [{ value: data.email }], // Generate username from email prefix
        // default_langcode: [{value: true}],
        field_firstname: [{ value: data.firstName || "" }],
        field_lastname: [{ value: data.lastName || "" }],
        field_job_title: [{ value: "" }], // Empty for now, will be filled in account setup
        field_marketing_update_emails: [
          { value: data.marketingUpdates || false },
        ],

        mail: [{ value: data.email || "" }],
        pass: [{ value: data.password || "" }],
      };
      // send company_name only when it is not empty
      if (data.companyName !== "") {
        payload = {
          ...payload,
          field_company_name: [{ value: data.companyName }],
        };
      }

      //console.log("Sending registration payload:", payload);

      // code for reCAPTCHA-v3 validation
      if (!executeRecaptcha) {
        //console.log("reCAPTCHA not loaded yet");
        return;
      }

      // Execute reCAPTCHA to get the token
      const recaptchaToken = await executeRecaptcha("submit");

      const valResponse =
        await reCaptchaV3ValidationService.recaptchaV3Validation(
          payload,
          recaptchaToken,
        );

      const resData = await valResponse;

      if (resData.status === "success") {
        const response = await apiService.post(
          API_ENDPOINTS.SIGNUP.REGISTER,
          payload,
          { headers: { Authorization: "" } },
        );

        //console.log("Registration successful:", response);

        // Set success state and show email verification message
        setRegistrationSuccess(true);
        setRegisteredEmail(data.email || "");

        //console.log(
        //  "Registration complete - showing email verification message",
        //);
      }
    } catch (error: any) {
      console.error("Registration failed:", error?.message);
      // Check if it's a 422 error with "is already taken" message (email already exists)

      if (error?.message.includes("is already taken")) {
        setError("email", {
          type: "manual",
          message:
            "This email address is already registered. Please use a different email or try logging in.",
        });
      } else {
        setRegistrationError(
          "Registration failed. Please check your information and try again.",
        );
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const renderContent = () => {
    // Show email verification success message if registration was successful
    if (registrationSuccess) {
      return (
        <div className="step-content email-verification-success">
          <div className="completion-icon">
            <i
              className="pi pi-envelope"
              style={{ fontSize: "3rem", color: "#4CAF50" }}
            ></i>
          </div>
          <h2 className="completion-title">Check your email</h2>
          <p className="completion-message">
            We've sent a verification email to{" "}
            <strong>{registeredEmail}</strong>
          </p>
          <p className="completion-message">
            Please check your inbox and click the verification link to activate
            your account.
          </p>
          <div
            className="verification-actions"
            style={{ marginBottom: "2rem" }}
          >
            <Link
              to={ROUTES.LOGIN}
              className="submit-button"
              style={{
                textDecoration: "none",
                display: "inline-block",
                textAlign: "center",
              }}
            >
              Go to Login
            </Link>
          </div>
          <p
            className="completion-message"
            style={{ fontSize: "0.9rem", marginBottom: "0" }}
          >
            Didn't receive the email? Check your spam folder or contact support.
          </p>
        </div>
      );
    }

    return (
      <>
        <SEO
          title={`Sign up | EUcalls`}
          description={`Join EUcalls to discover European funding opportunities, connect with project partners, and manage your project ideas through one powerful platform.`}
        />

        <h1 className="signup-title">Sign up</h1>
        <p className="signup-subtitle">
          Start your 14-day free Professional plan trial.
        </p>

        <form
          className="signup-form"
          onSubmit={handleSubmit(onSubmitInitialForm)}
        >
          <div className="form-group">
            <input
              type="text"
              placeholder="First name"
              className={`form-input ${errors.firstName ? "error" : ""}`}
              {...register("firstName", { required: "First name is required" })}
            />
            {errors.firstName && (
              <span className="error-message">{errors.firstName.message}</span>
            )}
          </div>

          <div className="form-group">
            <input
              type="text"
              placeholder="Last name"
              className={`form-input ${errors.lastName ? "error" : ""}`}
              {...register("lastName", { required: "Last name is required" })}
            />
            {errors.lastName && (
              <span className="error-message">{errors.lastName.message}</span>
            )}
          </div>

          {/* Hidden fake field for honeypot spam protection - not visible to users but bots might fill it in */}
          <div className="form-group" style={{ display: "none" }}>
            <input
              type="text"
              placeholder="Company name"
              className={`form-input ${errors.companyName ? "error" : ""}`}
              {...register("companyName", { required: false })}
            />
            {errors.companyName && (
              <span className="error-message">
                {errors.companyName.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <input
              type="email"
              placeholder="Email"
              className={`form-input ${errors.email ? "error" : ""}`}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
            />
            {errors.email && (
              <span className="error-message">{errors.email.message}</span>
            )}
            <span className="form-hint">
              Please use your organisation's email address
            </span>
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                className={`form-input ${errors.password ? "error" : ""}`}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                })}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                <i
                  className={`pi ${showPassword ? "pi-eye-slash" : "pi-eye"}`}
                ></i>
              </button>
            </div>
            {errors.password && (
              <span className="error-message">{errors.password.message}</span>
            )}
            <span className="form-hint">Must be at least 8 characters.</span>
          </div>

          <div className="form-group">
            <div className="input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm password"
                className={`form-input ${
                  errors.confirmPassword ? "error" : ""
                }`}
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === password || "Passwords do not match",
                })}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                <i
                  className={`pi ${
                    showConfirmPassword ? "pi-eye-slash" : "pi-eye"
                  }`}
                ></i>
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="error-message">
                {errors.confirmPassword.message}
              </span>
            )}
          </div>

          {/* <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input type="checkbox" {...register("marketingUpdates")} />
              <span>I want to receive marketing updates</span>
            </label>
          </div> */}

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                {...register("termsAccepted", {
                  required: "You must accept the terms and conditions",
                })}
              />
              <span>
                I agree to the{" "}
                <a href="https://eucalls.net/terms-of-service" target="_blank">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="https://eucalls.net/privacy-policy" target="_blank">
                  Privacy Policy
                </a>
              </span>
            </label>
            {errors.termsAccepted && (
              <span className="error-message">
                {errors.termsAccepted.message}
              </span>
            )}
          </div>

          {registrationError && (
            <div
              className="error-message"
              style={{ marginBottom: "1rem", color: "#e74c3c" }}
            >
              {registrationError}
            </div>
          )}
          <button
            type="submit"
            className="submit-button"
            disabled={isRegistering}
          >
            {isRegistering ? "Creating Account..." : "Sign up"}
          </button>
          <div
            className="recaptcha-info"
            style={{ fontSize: "0.8rem", marginTop: "1rem" }}
          >
            This site is protected by reCAPTCHA and the Google{" "}
            <span className="login-link">
              <Link to="https://policies.google.com/privacy" target="_blank">
                Privacy Policy
              </Link>
            </span>{" "}
            and{" "}
            <span className="login-link">
              <Link
                className="login-link"
                to="https://policies.google.com/terms"
                target="_blank"
              >
                Terms of Service
              </Link>
            </span>{" "}
            apply.
          </div>

          <div className="social-signup">
            <GoogleOAuthProvider clientId={VITE_PUBLIC_GOOGLE_LOGIN_CLIENT_ID}>
              <GoogleLoginButton isLoginPage={false} />
            </GoogleOAuthProvider>
            <FacebookLoginButton />
            <LinkedInLoginButton />
          </div>

          <div className="login-link">
            Already have an account? <Link to={ROUTES.LOGIN}>Log in</Link>
          </div>
        </form>
      </>
    );
  };

  return (
    <div className="signup">
      <div className="signup-wrapper">
        <div className="signup-left">
          <div className="signup-logo">
            <Link to={"https://eucalls.net/"}>
              <img src={imageList.logo} alt="EuCalls" />
            </Link>
          </div>

          <div className="signup-form-wrapper">{renderContent()}</div>

          <div className="signup-footer">
            &copy; {new Date().getFullYear()} EUcalls. All rights reserved.
            Powered by{" "}
            <a href="https://joistpark.eu/" target="_blank">
              JOIST
            </a>
          </div>
        </div>

        <div className="signup-right">
          <div className="signup-right-content">
            <span className="signup-right-content-dashboard-icon">
              <img src={imageList.dashboardIcon} alt="dashboard" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
